import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useEffect } from "react";
import { isIPad } from "@client/lib/isIPad";
import { trySetItem } from "@client/utils/storageWrite";
import {
  createRecentUrlDeduper,
  navigateInApp,
  parseUniversalLinkPath,
} from "./universalLinks";

// Mirrors useMobileAuthDeepLink's launch-URL contract (see the iphone
// AGENTS.md): `getLaunchUrl()` returns the SAME URL for the life of the
// process, so a cold-start link is marked consumed after navigation or a
// later reload/sign-out would replay it. Separate key from the auth hook —
// each hook consumes only the URLs it can parse.
const LAUNCH_URL_CONSUMED_KEY = "universalLinkLaunchUrlConsumed";

// Module-level like the auth hook's guard: Capacitor can deliver one
// cold-start link through BOTH getLaunchUrl and appUrlOpen (either order),
// and the window survives any effect remount in between.
const wasRecentlyHandled = createRecentUrlDeduper();

/**
 * Routes iOS Universal Link opens (https round links) into the
 * running app. Warm opens navigate immediately; cold starts navigate once
 * this hook mounts — after the boot redirect has already run, so the link's
 * destination wins over the remembered round.
 *
 * iPad only for the navigation itself: the iPhone shell's stack router has
 * no round routes, so a link there just foregrounds the app. Signed-out
 * taps land on the sign-in screen; the post-auth flow's own navigation
 * takes over from there (documented limitation — the link isn't replayed).
 */
export function useUniversalLinkOpen(): void {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    function applyUniversalLink(
      url: string | undefined,
      { coldStart }: { coldStart: boolean },
    ): void {
      if (!url) {
        return;
      }
      const path = parseUniversalLinkPath(url);
      if (!path) {
        return;
      }
      // Consume even on iPhone: the URL was recognized and handled (by
      // foregrounding), and must not replay navigation after a reload.
      // If the marker can't be written (storage pressure), don't act on the
      // URL at all: navigating without consuming creates a replayable state —
      // `getLaunchUrl()` returns this same URL for the life of the process,
      // so every later reload (sign-out included) would yank the user back
      // here. A skipped convenience navigation degrades gracefully; if
      // storage recovers, a later reload consumes and navigates normally.
      if (
        coldStart &&
        !trySetItem(window.sessionStorage, LAUNCH_URL_CONSUMED_KEY, "1")
      ) {
        return;
      }
      // The cold-start twin delivery (same URL via both channels, moments
      // apart) must navigate once. Checked after the consume mark — a
      // deduped launch URL still counts as handled.
      if (wasRecentlyHandled(url, Date.now())) {
        return;
      }
      if (!isIPad()) {
        // The iPhone shell has no round routes, so keeping the link would
        // kill it (the app just foregrounds). Hand it back to the system:
        // Capacitor opens non-allowlisted https navigations externally, and
        // iOS opens an app's OWN universal link in Safari instead of
        // bouncing it back into the app (documented self-open behavior).
        // If a platform quirk ever does bounce it, the dedupe window above
        // swallows the replay — one bounce, no loop.
        window.location.href = url;
        return;
      }
      // SPA navigation, not location.assign: a full reload would re-run
      // boot and lose in-memory state. navigateInApp reproduces the
      // navigation events consumers rely on — including the hashchange the
      // passage-landing engine needs when the target page is already
      // mounted. Cold starts REPLACE: the only entry beneath is the boot
      // redirect's remembered round, which the user didn't choose this
      // session and must not sit under Back.
      navigateInApp(path, { replace: coldStart });
    }

    // Warm open: the app was running (foreground or background) when the
    // link was tapped.
    const listenerHandle = App.addListener("appUrlOpen", ({ url }) => {
      applyUniversalLink(url, { coldStart: false });
    });

    // Cold start: the link launched the app, so `appUrlOpen` never fires.
    void App.getLaunchUrl().then((result) => {
      if (window.sessionStorage.getItem(LAUNCH_URL_CONSUMED_KEY)) {
        return;
      }
      applyUniversalLink(result?.url, { coldStart: true });
    });

    return () => {
      void listenerHandle.then((handle) => handle.remove());
    };
  }, []);
}
